import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { Button } from "@shadcn-in-nx/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@shadcn-in-nx/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@shadcn-in-nx/ui/select";
import { Label } from "@shadcn-in-nx/ui/label";
import { Loader2 } from "lucide-react";

function FunctionShareDialog({ workflowId, onClose, onCreated }) {
    const { t } = useTranslation();
    const [functions, setFunctions] = useState([]);
    const [workflows, setWorkflows] = useState([]);
    const [selectedFn, setSelectedFn] = useState("");
    const [selectedWorkflow, setSelectedWorkflow] = useState("");
    const [isLoadingData, setIsLoadingData] = useState(true);

    const { call: fetchFunctions } = useBluelagoon({ method: "getShareableFunctions" });
    const { call: fetchWorkflows } = useBluelagoon({ method: "getCompanyWorkflows" });
    const { call: createGrant, isLoading: isCreating } = useBluelagoon({ method: "createFunctionGrant" });

    useEffect(() => {
        async function load() {
            setIsLoadingData(true);
            try {
                const [fnRes, wfRes] = await Promise.all([
                    fetchFunctions({ workflowId }),
                    fetchWorkflows({}),
                ]);
                setFunctions(fnRes?.data || []);
                setWorkflows((wfRes?.data?.workflows || []).filter((w) => w.id !== workflowId));
            } catch {
                toast.error(t("appPreview.loadDataFailed"));
            } finally {
                setIsLoadingData(false);
            }
        }
        load();
    }, [workflowId, fetchFunctions, fetchWorkflows]);

    const handleShare = async () => {
        if (!selectedFn || !selectedWorkflow) return;
        try {
            const res = await createGrant({
                componentId: selectedFn,
                sourceWorkflowId: workflowId,
                grantedWorkflowId: selectedWorkflow,
            });
            if (res?.success) {
                toast.success(t("appPreview.functionSharedSuccess"));
                onCreated();
                onClose();
            } else {
                toast.error(res?.error || t("appPreview.functionShareFailed"));
            }
        } catch {
            toast.error(t("appPreview.functionShareFailed"));
        }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent>
                <DialogHeader><DialogTitle>{t("appPreview.shareAFunction")}</DialogTitle></DialogHeader>
                {isLoadingData ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : (
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>{t("appPreview.selectFunction")}</Label>
                            <Select value={selectedFn} onValueChange={setSelectedFn}>
                                <SelectTrigger><SelectValue placeholder={t("appPreview.selectFunctionPlaceholder")} /></SelectTrigger>
                                <SelectContent>
                                    {functions.map((fn) => (
                                        <SelectItem key={fn.id} value={fn.id}>{fn.name} ({fn.filePath || fn.type})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>{t("appPreview.targetApp")}</Label>
                            <Select value={selectedWorkflow} onValueChange={setSelectedWorkflow}>
                                <SelectTrigger><SelectValue placeholder={t("appPreview.selectTargetApp")} /></SelectTrigger>
                                <SelectContent>
                                    {workflows.map((w) => (
                                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
                    <Button onClick={handleShare} disabled={!selectedFn || !selectedWorkflow || isCreating}>
                        {isCreating ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />{t("appPreview.sharing")}</> : t("appPreview.share")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default FunctionShareDialog;
